/**
 * <variant-picker> (snippets/variant-picker.liquid): a dialog to pick one option value. On
 * confirm it dispatches `variant-picker:confirm`; the page itself is never changed.
 *
 * The swatches are role="radio" buttons, not inputs: the legacy add-to-cart reads
 * `input:checked` inside the configurator cards.
 */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    // Runs again when the element is moved: bind once.
    if (this.ready) return;

    this.dialog = this.querySelector('[data-variant-picker-dialog]');
    const dataElement = this.querySelector('[data-variant-picker-data]');
    if (!this.dialog || !dataElement) return;

    try {
      this.data = JSON.parse(dataElement.textContent);
    } catch {
      return;
    }

    this.ready = true;
    this.currentVariantId = this.data.variantId;
    this.selectedValue = null;
    this.selectedVariant = null;

    this.swatches = Array.from(this.querySelectorAll('[role="radio"]'));
    this.confirmButton = this.querySelector('[data-variant-picker-confirm]');
    this.moreButton = this.querySelector('[data-variant-picker-more]');
    this.imageElement = this.querySelector('[data-variant-picker-image]');
    this.nameElement = this.querySelector('[data-variant-picker-name]');
    this.priceElement = this.querySelector('[data-variant-picker-price-current]');
    this.discountElement = this.querySelector('[data-variant-picker-discount]');
    this.compareElement = this.querySelector('[data-variant-picker-price-compare]');
    this.savingsElement = this.querySelector('[data-variant-picker-price-savings]');
    this.unitPriceElement = this.querySelector('[data-variant-picker-unit-price]');

    this.querySelectorAll('[data-variant-picker-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.open());
    });
    this.querySelector('[data-variant-picker-close]').addEventListener('click', () => this.close());
    this.confirmButton.addEventListener('click', () => this.confirm());
    if (this.moreButton) {
      this.moreButton.addEventListener('click', () =>
        this.setExpanded(this.moreButton.getAttribute('aria-expanded') !== 'true'),
      );
    }

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => this.select(swatch.dataset.value));
    });
    this.querySelector('[role="radiogroup"]').addEventListener('keydown', (event) =>
      this.handleKeydown(event),
    );

    // A click on the backdrop targets the dialog itself.
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => {
      document.body.style.overflow = this.previousOverflow || '';
    });
  }

  /** The last confirmed variant, or the one it started with. */
  get variantId() {
    return this.currentVariantId;
  }

  /** In stock with at least the host's `min-stock` units (no attribute = 0; stock null = not limited). */
  isPickable(variant) {
    const minStock = Number(this.getAttribute('min-stock'));
    return variant.available && (variant.stock === null || variant.stock >= minStock);
  }

  open() {
    if (!this.ready || this.dialog.open) return;

    this.syncSwatches();

    const current = this.variantById(this.currentVariantId);
    const currentValue = current && current.options[this.data.optionIndex];
    const start =
      this.swatchFor(currentValue, true) ||
      this.swatches.find((swatch) => !swatch.disabled) ||
      this.swatchFor(currentValue);

    // Don't leave the selection hidden behind "+N".
    if (start && start.hidden) this.setExpanded(true);
    if (start) this.select(start.dataset.value);
    else this.render();

    this.previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.dialog.showModal();
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  confirm() {
    const variant = this.selectedVariant;
    if (!variant || !this.isPickable(variant)) return;

    const changed = variant.id !== this.currentVariantId;
    this.currentVariantId = variant.id;
    this.close();

    this.dispatchEvent(
      new CustomEvent('variant-picker:confirm', {
        bubbles: true,
        detail: {
          variantId: variant.id,
          productId: this.data.productId,
          optionName: this.data.optionName,
          optionValue: this.selectedValue,
          changed,
          variant,
        },
      }),
    );
  }

  select(value) {
    this.selectedValue = value;
    this.selectedVariant = this.resolve(value);

    this.swatches.forEach((swatch) => {
      const checked = swatch.dataset.value === value;
      swatch.setAttribute('aria-checked', String(checked));
      swatch.tabIndex = checked ? 0 : -1;
    });
    this.render();
  }

  /** Shows or hides the values beyond max_visible ("+N" turns into "−"). */
  setExpanded(expanded) {
    this.querySelectorAll('[data-variant-picker-extra]').forEach((swatch) => {
      swatch.hidden = !expanded;
    });
    if (!this.moreButton) return;
    this.moreButton.setAttribute('aria-expanded', String(expanded));
    this.moreButton.querySelector('.variant-picker__more-count').hidden = expanded;
    this.moreButton.querySelector('.variant-picker__more-less').hidden = !expanded;
  }

  /** Disables the values without a pickable variant. Runs on every open: `min-stock` can change in between. */
  syncSwatches() {
    const index = this.data.optionIndex;
    this.swatches.forEach((swatch) => {
      swatch.disabled = !this.data.variants.some(
        (variant) => variant.options[index] === swatch.dataset.value && this.isPickable(variant),
      );
    });
  }

  swatchFor(value, enabledOnly = false) {
    return this.swatches.find(
      (swatch) => swatch.dataset.value === value && !(enabledOnly && swatch.disabled),
    );
  }

  variantById(id) {
    return this.data.variants.find((variant) => variant.id === id);
  }

  /** The variant for picking `value`: keeps the other options, else any available one with it. */
  resolve(value) {
    const index = this.data.optionIndex;
    const current = this.variantById(this.currentVariantId);
    const keepsOtherOptions = (variant) =>
      !current ||
      variant.options.every(
        (option, position) => position === index || option === current.options[position],
      );

    const candidates = this.data.variants.filter((variant) => variant.options[index] === value);
    return (
      candidates.find((variant) => this.isPickable(variant) && keepsOtherOptions(variant)) ||
      candidates.find((variant) => this.isPickable(variant)) ||
      candidates.find(keepsOtherOptions) ||
      candidates[0] ||
      null
    );
  }

  render() {
    const variant = this.selectedVariant;

    if (this.nameElement) this.nameElement.textContent = this.selectedValue || '';

    if (this.imageElement) {
      this.imageElement.hidden = !(variant && variant.image);
      if (variant && variant.image) this.imageElement.src = variant.image;
    }

    if (this.priceElement) {
      const onSale = Boolean(variant && variant.compareAt > variant.price);
      this.priceElement.textContent = variant ? variant.priceText : '';
      this.priceElement.parentElement.classList.toggle('variant-picker__price--sale', onSale);

      // Only rendered when price and discount are both shown.
      if (this.discountElement) {
        this.discountElement.hidden = !onSale;
        this.compareElement.textContent = onSale ? variant.compareAtText : '';
        this.savingsElement.textContent = onSale
          ? `–${Math.round(((variant.compareAt - variant.price) * 100) / variant.compareAt)} %`
          : '';
      }
    }

    if (this.unitPriceElement) {
      const unitPrice = variant ? variant.unitPrice : '';
      this.unitPriceElement.hidden = !unitPrice;
      this.unitPriceElement.textContent = unitPrice;
    }

    this.confirmButton.disabled = !variant || !this.isPickable(variant);
  }

  /** Arrow keys move through the values, skipping sold-out ones. */
  handleKeydown(event) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    const choices = this.swatches.filter((swatch) => !swatch.disabled && !swatch.hidden);
    if (!choices.length || (!step && event.key !== 'Home' && event.key !== 'End')) return;

    const position = choices.indexOf(document.activeElement);
    let next;
    if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = choices.length - 1;
    else next = (position + step + choices.length) % choices.length;

    event.preventDefault();
    choices[next].focus();
    this.select(choices[next].dataset.value);
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
